/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  ATTRIBUTE_KEY,
  TAG_ATTRIBUTE,
  PRODUCT_ATTRIBUTE,
  PRODUCT_CATEGORY,
  ProductCategoryValue,
  RPODUCT_ATTRIBUTE_TYPE
} from '@/data/const'
import { NewWorksItemWithCondition, StackingSet } from '@/data/newworks'
import { FORMATTED_PRODUCT_SERIE } from '@/data/series'
import { StackingSetKey } from '@/data/stacking'
import {
  FormattedProduct,
  FormattedStacking,
  useProductStore,
  useSeriesStore,
  useStackingStore
} from '@/store'
import { computed } from 'vue'

const useSelect = () => {
  const seriesStore = useSeriesStore()
  const productStore = useProductStore()
  const stackingStore = useStackingStore()

  // 当前选择的产品ID
  const selectedId = computed(
    () =>
      stackingStore.selectedStacking?.guid ||
      productStore.selectedProduct?.skuId
  )

  // 是否是面部的试戴
  const isFaceTryOn = computed(() => {
    return (
      productStore.selectedCategory &&
      [PRODUCT_CATEGORY.EARRING.value].includes(productStore.selectedCategory)
    )
  })

  const selectedTag = computed(
    () =>
      productStore.selectedAttributes
        .map(label => PRODUCT_ATTRIBUTE[label])
        .find(attr => attr?.type === RPODUCT_ATTRIBUTE_TYPE.CUSTOM_TAG) as
        | TAG_ATTRIBUTE
        | undefined
  )

  // 是否是手部的试戴
  const isHandTryOn = computed(() => {
    if (selectedTag.value) {
      return selectedTag.value.isHand
    }
    return (
      productStore.selectedCategory &&
      [
        PRODUCT_CATEGORY.WATCH.value,
        PRODUCT_CATEGORY.RING.value,
        PRODUCT_CATEGORY.BRACELET.value
      ].includes(productStore.selectedCategory)
    )
  })

  const selectedStackingSetKey = computed(
    () => stackingStore.selectedStackingSet?.key
  )

  const isStackingsSelected = computed(() => {
    return selectedStackingSetKey.value != undefined
  })

  const isStackingsSelectable = computed(
    () =>
      isStackingsSelected.value &&
      !productStore.isQueryEmpty &&
      selectableStackings.value.length > 1
  )

  const hasSelectedStacking = computed(
    () => stackingStore.selectedStacking?.guid !== undefined
  )

  const isSerieSelected = computed(
    () => (seriesStore.selectedSerialProduct?.variants.length || 0) > 0
  )

  const hasSelectedProduct = computed(() => {
    return productStore.selectedProduct?.skuId
  })

  const selectableProducts = computed<
    Array<FormattedProduct & { materialIcon?: string }>
  >(() => {
    if (isStackingsSelected.value) {
      // console.log('[[ Selection ] Filtered Empty Products ]', Date.now())
      return []
    }

    if (isSerieSelected.value) {
      return [...seriesStore.selectedSerie]
    }
    return [...productStore.filteredProducts]
  })

  const isProductsSelectable = computed(
    () =>
      !isStackingsSelected.value &&
      !productStore.isQueryEmpty &&
      selectableProducts.value.length > 0
  )

  const selectableStackings = computed(() => stackingStore.selectedStackings)

  const resetWithAttributes = (attrs: ATTRIBUTE_KEY[]) => {
    console.log('[[ Selection ] Reset With Attributes ]', { attrs }, Date.now())
    const stackingSetChanged = stackingStore.reset()
    const serieChanged = seriesStore.reset()
    const attrChanged = productStore.setSelectedAttributes(attrs)
    return stackingSetChanged || serieChanged || attrChanged
  }

  const resetAll = () => {
    console.log('[[ Selection ] Reset All Store ]', Date.now())
    const stackingSetChanged = stackingStore.reset()
    const serieChanged = seriesStore.reset()
    const apcChanged = productStore.reset()
    return stackingSetChanged || serieChanged || apcChanged
  }

  const selectStackingSet = (item: StackingSet) => {
    // console.log(
    //   '[[ Selection ] Select StackingSet ]',
    //   { key: item.key },
    //   Date.now()
    // )
    seriesStore.reset()
    if (stackingStore.setSelectedStackingSetByKey(item.key as StackingSetKey)) {
      productStore.reset()
      productStore.setSelectedCategory(
        item.query.categories[0].value as ProductCategoryValue,
        item.key.toLowerCase()
      )
      return true
    }
    return false
  }

  const selectCategory = (category: ProductCategoryValue, source: string) => {
    // console.log(
    //   '[[ Selection ] Select Category ]',
    //   {
    //     category,
    //     fromNewWorks
    //   },
    //   Date.now()
    // )
    const partialChanged = resetWithAttributes([])

    return productStore.setSelectedCategory(category, source) || partialChanged
  }

  const selectCategoryAndAttr = (item: NewWorksItemWithCondition) => {
    const attrs = (item.query?.attributes || []).map(
      attr => attr.label
    ) as ATTRIBUTE_KEY[]
    console.log(
      '[[ Selection ] Select Category And Attributes ]',
      {
        attrs,
        key: item.key,
        category: item.query.categories[0]?.value
      },
      Date.now()
    )
    const partialChanged = resetWithAttributes(attrs)
    if (item.query?.categories?.[0]) {
      return (
        productStore.setSelectedCategory(
          item.query.categories[0].value as ProductCategoryValue,
          item.key.toLowerCase()
        ) || partialChanged
      )
    } else {
      productStore.resetSelectedCategory(item.key.toLowerCase())
    }
    return partialChanged
  }

  const selectSeries = (data: FORMATTED_PRODUCT_SERIE, source: string) => {
    // console.log(
    //   '[[ Selection ] Select Series ]',
    //   {
    //     skuId: data.skuId
    //   },
    //   Date.now()
    // )
    stackingStore.reset()
    if (seriesStore.setSelectedProductOfASerie(data)) {
      const product = seriesStore.selectedSerie.find(
        p => p.skuId === data.skuId
      )!
      console.log(
        '[[ Selection ] Select Serial Product ]',
        {
          skuId: data.skuId,
          category: product.category.value
        },
        Date.now()
      )
      productStore.setSelectedCategory(
        product.category.value as ProductCategoryValue,
        source
      )
      productStore.setSelectedAttributes([])
      return true
    }
    return false
  }

  const selectStacking = (stacking: FormattedStacking) => {
    // console.log(
    //   '[[ Selection ] Select Stacking ]',
    //   { guid: stacking.guid },
    //   Date.now()
    // )
    seriesStore.reset()
    productStore.resetPartial()
    if (stackingStore.setSelectedStacking(stacking)) {
      return true
    }
    return false
  }

  const selectProduct = (product: FormattedProduct) => {
    // console.log(
    //   '[[ Selection ] Select Product / accept ]',
    //   { skuId: product.skuId },
    //   Date.now()
    // )
    stackingStore.reset()
    return productStore.setSelectedProduct(product)
  }

  return {
    isFaceTryOn,
    isHandTryOn,
    isStackingsSelected,
    isStackingsSelectable,
    hasSelectedStacking,
    isSerieSelected,
    isProductsSelectable,
    hasSelectedProduct,
    selectableProducts,
    selectedStackingSetKey,
    selectableStackings,
    selectedId,
    selectCategory,
    selectCategoryAndAttr,
    selectStackingSet,
    selectSeries,
    selectProduct,
    selectStacking,
    reset: resetAll
  }
}

export default useSelect
